import React, { Fragment } from 'react'
import {
  Box, Grid, InputAdornment, MenuItem, Table, TableBody, TableCell, TableHead, TableRow, TextField, Typography,
} from '@mui/material'
import { T } from '../PartnerSignups/onboardingDesign'
import { Badge, FieldLabel } from '../PartnerSignups/onboardingUi'
import { COMMISSION_KIND, day, headCell, money, pct, plural, tableSx } from './PortalParts'

/**
 * ISO pieces shared by the super admin page (views/Agents.jsx) and the ISO
 * and agent portals (views/IsoPortal.jsx, views/AgentPortal.jsx): the rates
 * per platform, bank fields, a month's accounts and the payouts list.
 * Backend: resolvers/iso.js, utils/isoEarnings.js.
 */

export const PLATFORMS = [
  { key: 'RUSHMYFOOD', label: 'RushMyFood', hint: 'App, website and the restaurant’s own website' },
  { key: 'POS', label: 'POS', hint: 'In-store orders on the RushMyFood POS' },
  { key: 'DOORDASH', label: 'DoorDash', hint: 'DoorDash orders we manage' },
  { key: 'UBEREATS', label: 'Uber Eats', hint: 'Uber Eats orders we manage' },
  { key: 'GRUBHUB', label: 'Grubhub', hint: 'Grubhub orders we manage' },
]

export const ISO_RATE_FIELDS = 'platform label percent perOrder'
export const BANK_FIELDS = 'bankName holderName accountType routing account accountLast4 isSet'
const PLATFORM_FIELDS = 'platform label orders subtotal tips percent perOrder amount'
export const MONTH_FIELDS = `
  period label orders subtotal tips gross agentShares agentBonuses carryIn isoAmount carryOut payoutTo agentsPaidBy
  platforms { ${PLATFORM_FIELDS} }
  accounts { linkId restaurantId restaurantName agentId agentName split orders subtotal tips extra amount agentShare since endedAt
    ordersToDate bonusAmount bonusOrders bonusStatus
    platforms { ${PLATFORM_FIELDS} } }
  agents { agentId agentName accounts orders subtotal tips amount share }
`
export const STATEMENT_FIELDS = `
  _id period label payoutTo orders subtotal tips gross agentShares agentBonuses carryIn isoAmount carryOut createdAt
  platforms { ${PLATFORM_FIELDS} }
  accounts { linkId restaurantId restaurantName agentId agentName split orders subtotal tips extra amount agentShare
    platforms { ${PLATFORM_FIELDS} } }
`
export const PAYOUT_FIELDS = '_id isoId isoName kind payee agentId agentName payer period periodLabel restaurantId restaurantName partnerSignupId amount status note createdAt paidAt'
export const ISO_AGENT_FIELDS = '_id name email phone code link split bonus bonusOrders isActive isEmailVerified lastLoginAt createdAt bankSet stats { invites started signed approved rejected pendingAmount paidAmount restaurants signedUp assigned }'

/** "$100 after 50 orders", or "None". */
export function bonusText(amount, orders) {
  if (!Number(amount)) return 'None'
  return `${money(amount)} after ${orders || 50} orders`
}

const ACCOUNT_TYPES = [
  { value: 'BUSINESS_CHECKING', label: 'Business checking' },
  { value: 'CHECKING', label: 'Personal checking' },
]

/** "5% + $0.25 per order", or "—" when nothing is earned on the platform. */
export function rateText(r) {
  const p = Number(r && r.percent) || 0
  const f = Number(r && r.perOrder) || 0
  if (!p && !f) return '—'
  return [p ? pct(p) : null, f ? `${money(f)} per order` : null].filter(Boolean).join(' + ')
}

/** The rates as form values: { RUSHMYFOOD: { percent: '5', perOrder: '0.25' }, ... }. */
export function ratesToForm(rates) {
  const byKey = Object.fromEntries((rates || []).map(r => [r.platform, r]))
  return Object.fromEntries(PLATFORMS.map(p => [p.key, {
    percent: byKey[p.key] && Number(byKey[p.key].percent) ? String(byKey[p.key].percent) : '',
    perOrder: byKey[p.key] && Number(byKey[p.key].perOrder) ? String(byKey[p.key].perOrder) : '',
  }]))
}

export function formToRates(form) {
  return PLATFORMS.map(p => ({
    platform: p.key,
    percent: form[p.key].percent === '' ? 0 : Number(form[p.key].percent),
    perOrder: form[p.key].perOrder === '' ? 0 : Number(form[p.key].perOrder),
  }))
}

/** What the ISO earns on each platform: a % of the subtotal and a flat amount per order. */
export function RatesEditor({ value, onChange }) {
  const set = (key, field) => e => onChange({ ...value, [key]: { ...value[key], [field]: e.target.value } })
  return (
    <Box>
      {PLATFORMS.map(p => (
        <Grid container key={p.key} columnSpacing={1.5} rowSpacing={0.5} alignItems="center" sx={{ py: 1, borderTop: `1px solid ${T.divider}`, '&:first-of-type': { borderTop: 0 } }}>
          <Grid item xs={12} sm={4}>
            <Typography sx={{ fontWeight: 600, fontSize: 14.5, color: T.text }}>{p.label}</Typography>
            <Typography sx={{ color: T.hint, fontSize: 12.5 }}>{p.hint}</Typography>
          </Grid>
          <Grid item xs={6} sm={4}>
            <TextField
              fullWidth size="small" type="number" value={value[p.key].percent} onChange={set(p.key, 'percent')} placeholder="0"
              inputProps={{ min: 0, max: 100, step: 0.25, 'aria-label': `${p.label}: % of subtotal` }}
              InputProps={{ endAdornment: <InputAdornment position="end">% of subtotal</InputAdornment> }}
            />
          </Grid>
          <Grid item xs={6} sm={4}>
            <TextField
              fullWidth size="small" type="number" value={value[p.key].perOrder} onChange={set(p.key, 'perOrder')} placeholder="0.00"
              inputProps={{ min: 0, step: 0.05, 'aria-label': `${p.label}: per order` }}
              InputProps={{ startAdornment: <InputAdornment position="start">$</InputAdornment>, endAdornment: <InputAdornment position="end">/ order</InputAdornment> }}
            />
          </Grid>
        </Grid>
      ))}
    </Box>
  )
}

/** The rates, read only. */
export function RatesTable({ rates }) {
  return (
    <Box>
      {PLATFORMS.map(p => {
        const r = (rates || []).find(x => x.platform === p.key)
        return (
          <Box key={p.key} sx={{ display: 'flex', justifyContent: 'space-between', gap: 2, py: 0.9, borderTop: `1px solid ${T.divider}`, '&:first-of-type': { borderTop: 0 } }}>
            <Box sx={{ minWidth: 0 }}>
              <Typography sx={{ fontWeight: 600, fontSize: 14.5, color: T.text }}>{p.label}</Typography>
              <Typography sx={{ color: T.hint, fontSize: 12.5 }}>{p.hint}</Typography>
            </Box>
            <Typography sx={{ fontWeight: 600, fontSize: 14.5, color: rateText(r) === '—' ? T.hint : T.text, whiteSpace: 'nowrap' }}>{rateText(r)}</Typography>
          </Box>
        )
      })}
    </Box>
  )
}

export const emptyBank = { bankName: '', holderName: '', accountType: 'BUSINESS_CHECKING', routing: '', account: '' }

export function bankToForm(bank) {
  if (!bank) return { ...emptyBank }
  return { bankName: bank.bankName || '', holderName: bank.holderName || '', accountType: bank.accountType || 'BUSINESS_CHECKING', routing: bank.routing || '', account: bank.account || '' }
}

/**
 * Bank name, holder, type, routing and account. Stored numbers come back
 * masked (••••1234); leaving them as they are keeps them.
 */
export function BankFields({ value, onChange, idPrefix = 'bank' }) {
  const set = k => e => onChange({ ...value, [k]: e.target.value })
  const secret = { autoComplete: 'off', spellCheck: false }
  return (
    <Grid container columnSpacing={1.5} rowSpacing={1.75}>
      <Grid item xs={12} sm={6}>
        <FieldLabel htmlFor={`${idPrefix}-name`} label="Bank name" />
        <TextField id={`${idPrefix}-name`} fullWidth size="small" value={value.bankName} onChange={set('bankName')} />
      </Grid>
      <Grid item xs={12} sm={6}>
        <FieldLabel htmlFor={`${idPrefix}-type`} label="Account type" />
        <TextField id={`${idPrefix}-type`} select fullWidth size="small" value={value.accountType} onChange={set('accountType')}>
          {ACCOUNT_TYPES.map(t => <MenuItem key={t.value} value={t.value}>{t.label}</MenuItem>)}
        </TextField>
      </Grid>
      <Grid item xs={12}>
        <FieldLabel htmlFor={`${idPrefix}-holder`} label="Name on the account" />
        <TextField id={`${idPrefix}-holder`} fullWidth size="small" value={value.holderName} onChange={set('holderName')} />
      </Grid>
      <Grid item xs={12} sm={6}>
        <FieldLabel htmlFor={`${idPrefix}-routing`} label="Routing number" />
        <TextField id={`${idPrefix}-routing`} fullWidth size="small" value={value.routing} onChange={set('routing')} inputProps={{ ...secret, inputMode: 'numeric' }} />
      </Grid>
      <Grid item xs={12} sm={6}>
        <FieldLabel htmlFor={`${idPrefix}-account`} label="Account number" />
        <TextField id={`${idPrefix}-account`} fullWidth size="small" value={value.account} onChange={set('account')} inputProps={{ ...secret, inputMode: 'numeric' }} />
      </Grid>
    </Grid>
  )
}

const PAYOUT_KIND = {
  ISO_MONTH: 'Month to the ISO',
  AGENT_SHARE: 'Agent’s share of a month',
  AGENT_BONUS: 'Agent bonus',
}

/**
 * Each platform's orders, subtotal and tips: "RushMyFood 36 orders". withEarned:
 * also what the ISO earned on them (not for an ISO's agent).
 */
export function PlatformStats({ month, withEarned = true }) {
  const rows = (month && month.platforms) || []
  if (rows.length === 0) return null
  const heads = ['Platform', 'Orders', 'Subtotal', 'Tips', ...(withEarned ? ['Earned'] : [])]
  const cell = (v, bold) => <TableCell align="right" sx={{ fontWeight: bold ? 700 : 400, whiteSpace: 'nowrap' }}>{v}</TableCell>
  return (
    <Table size="small" sx={{ minWidth: 520, ...tableSx }}>
      <TableHead>
        <TableRow>
          {heads.map((h, i) => <TableCell key={h} sx={headCell} align={i === 0 ? 'left' : 'right'}>{h}</TableCell>)}
        </TableRow>
      </TableHead>
      <TableBody>
        {rows.map(p => (
          <TableRow key={p.platform}>
            <TableCell sx={{ fontWeight: 600 }}>{p.label}</TableCell>
            {cell(p.orders)}
            {cell(money(p.subtotal))}
            {cell(money(p.tips))}
            {withEarned && cell(money(p.amount))}
          </TableRow>
        ))}
        {rows.length > 1 && (
          <TableRow sx={{ '& .MuiTableCell-root': { bgcolor: T.footerBg } }}>
            <TableCell sx={{ fontWeight: 700 }}>All platforms</TableCell>
            {cell(month.orders, true)}
            {cell(money(month.subtotal), true)}
            {cell(money(month.tips), true)}
            {withEarned && cell(money(month.gross), true)}
          </TableRow>
        )}
      </TableBody>
    </Table>
  )
}

/** "34 of 50 orders to the $100 bonus", or that it was reached. */
function BonusProgress({ a }) {
  if (!a.bonusAmount || !a.bonusOrders) return null
  if (a.bonusStatus) {
    const paid = a.bonusStatus === 'PAID'
    return <Typography sx={{ color: paid ? T.green : T.amberText, fontSize: 12.5 }}>{money(a.bonusAmount)} bonus reached at {a.bonusOrders} orders{paid ? ', paid' : ', owed'}</Typography>
  }
  return <Typography sx={{ color: T.text2, fontSize: 12.5 }}>{Math.min(a.ordersToDate || 0, a.bonusOrders)} of {a.bonusOrders} orders to the {money(a.bonusAmount)} bonus</Typography>
}

/**
 * Payouts: what is owed, to whom, and who pays it. actions(p) renders the
 * buttons for a row, or nothing. showIso: the super admin's list of every ISO.
 */
export function PayoutsTable({ payouts, actions, showIso = false, emptyText = 'Nothing here yet.' }) {
  if (payouts.length === 0) return <Typography sx={{ p: 5, textAlign: 'center', color: T.hint }}>{emptyText}</Typography>
  const heads = ['Earned', ...(showIso ? ['ISO'] : []), 'To', 'For', 'Paid by', 'Amount', 'Status', '']
  return (
    <Table size="small" sx={{ minWidth: showIso ? 980 : 860, ...tableSx }}>
      <TableHead>
        <TableRow>
          {heads.map((h, i) => <TableCell key={`${h}${i}`} sx={headCell}>{h}</TableCell>)}
        </TableRow>
      </TableHead>
      <TableBody>
        {payouts.map(p => {
          const [label, kind] = COMMISSION_KIND[p.status] || COMMISSION_KIND.PENDING
          return (
            <TableRow key={p._id}>
              <TableCell sx={{ whiteSpace: 'nowrap', color: T.text2 }}>{day(p.createdAt)}</TableCell>
              {showIso && <TableCell sx={{ fontWeight: 600 }}>{p.isoName}</TableCell>}
              <TableCell sx={{ fontWeight: 600 }}>{p.payee === 'ISO' ? (showIso ? 'The ISO' : 'You') : p.agentName || 'Agent'}</TableCell>
              <TableCell>
                <Typography sx={{ fontWeight: 600, fontSize: 14 }}>{p.kind === 'AGENT_BONUS' ? p.restaurantName || 'Account' : p.periodLabel}</Typography>
                <Typography sx={{ color: T.hint, fontSize: 13 }}>
                  {PAYOUT_KIND[p.kind] || p.kind}{p.kind === 'AGENT_BONUS' && p.note ? `, ${p.note.toLowerCase()}` : ''}
                </Typography>
              </TableCell>
              <TableCell sx={{ whiteSpace: 'nowrap' }}>
                {p.payer === 'RUSHMYFOOD' ? 'RushMyFood' : <Badge kind="neutral">The ISO</Badge>}
              </TableCell>
              <TableCell sx={{ fontWeight: 600, whiteSpace: 'nowrap' }}>{money(p.amount)}</TableCell>
              <TableCell><Badge kind={kind}>{p.status === 'PAID' ? `Paid ${day(p.paidAt)}` : p.status === 'PENDING' ? 'Owed' : label}</Badge></TableCell>
              <TableCell align="right" sx={{ whiteSpace: 'nowrap' }}>{actions ? actions(p) : null}</TableCell>
            </TableRow>
          )
        })}
      </TableBody>
    </Table>
  )
}

/**
 * A month's accounts: orders, subtotal and tips (by platform), what the ISO
 * earned, the agent's share and their progress toward the agent's bonus.
 * forAgent: an ISO agent's own view. forAdmin: a super admin's ("the ISO"
 * rather than "you").
 */
export function MonthAccounts({ accounts, forAgent = false, forAdmin = false, isoName }) {
  if (!accounts || accounts.length === 0) {
    return <Typography sx={{ px: { xs: 2, sm: 3 }, py: 4, textAlign: 'center', color: T.hint }}>No approved accounts earning yet.</Typography>
  }
  const heads = forAgent
    ? ['Account', 'Orders', 'Subtotal', 'Tips', `${isoName || 'The ISO'} earned`, 'Your split', 'You earn']
    : ['Account', 'Agent', 'Orders', 'Subtotal', 'Tips', forAdmin ? 'ISO earns' : 'You earn', 'Agent’s share']
  return (
    <Table size="small" sx={{ minWidth: 800, ...tableSx }}>
      <TableHead>
        <TableRow>
          {heads.map((h, i) => <TableCell key={h} sx={headCell} align={i >= heads.length - 2 ? 'right' : 'left'}>{h}</TableCell>)}
        </TableRow>
      </TableHead>
      <TableBody>
        {accounts.map(a => (
          <Fragment key={`${a.linkId || ''}-${a.restaurantId}`}>
            <TableRow>
              <TableCell>
                <Typography sx={{ fontWeight: 600, fontSize: 14.5 }}>{a.restaurantName || `Restaurant #${a.restaurantId}`}</Typography>
                {(a.platforms || []).filter(x => x.orders > 0).map(x => (
                  <Typography key={x.platform} sx={{ color: T.hint, fontSize: 12.5 }}>
                    {x.label}: {plural(x.orders, 'order')}, {money(x.subtotal)}, tips {money(x.tips)}{forAgent ? '' : ` → ${money(x.amount)} (${rateText(x)})`}
                  </Typography>
                ))}
                {a.extra > 0 && <Typography sx={{ color: T.hint, fontSize: 12.5 }}>Above the minimums: +{money(a.extra)}</Typography>}
                <BonusProgress a={a} />
                {a.endedAt && <Typography sx={{ color: T.amberText, fontSize: 12.5 }}>Stopped {day(a.endedAt)}</Typography>}
              </TableCell>
              {!forAgent && <TableCell sx={{ color: a.agentName ? T.text : T.hint, whiteSpace: 'nowrap' }}>{a.agentName ? `${a.agentName} (${pct(a.split)})` : forAdmin ? 'The ISO' : 'You'}</TableCell>}
              <TableCell>{a.orders}</TableCell>
              <TableCell>{money(a.subtotal)}</TableCell>
              <TableCell>{money(a.tips)}</TableCell>
              {forAgent ? (
                <>
                  <TableCell>{money(a.amount)}</TableCell>
                  <TableCell align="right">{pct(a.split)}</TableCell>
                  <TableCell align="right" sx={{ fontWeight: 600 }}>{money(a.agentShare)}</TableCell>
                </>
              ) : (
                <>
                  <TableCell align="right" sx={{ fontWeight: 600 }}>{money(a.amount)}</TableCell>
                  <TableCell align="right" sx={{ color: a.agentId ? T.text : T.hint }}>{a.agentId ? money(a.agentShare) : '—'}</TableCell>
                </>
              )}
            </TableRow>
          </Fragment>
        ))}
      </TableBody>
    </Table>
  )
}

/**
 * Finished months, newest first: gross, the agents' part and what was owed
 * to the ISO. forIso: the ISO's own view ("you").
 */
export function StatementsList({ statements, forIso = false }) {
  if (!statements || statements.length === 0) return null
  return (
    <Table size="small" sx={{ minWidth: 960, ...tableSx }}>
      <TableHead>
        <TableRow>
          {['Month', 'Orders', 'Subtotal', 'Tips', 'Earned', 'Agents’ share', 'Bonuses RushMyFood paid', 'Carried over', forIso ? 'Owed to you' : 'Owed to the ISO'].map((h, i) => (
            <TableCell key={h} sx={headCell} align={i === 0 ? 'left' : 'right'}>{h}</TableCell>
          ))}
        </TableRow>
      </TableHead>
      <TableBody>
        {statements.map(s => (
          <TableRow key={s._id}>
            <TableCell>
              <Typography sx={{ fontWeight: 600, fontSize: 14.5 }}>{s.label}</Typography>
              <Typography sx={{ color: T.hint, fontSize: 12.5 }}>{s.payoutTo === 'AGENTS' ? 'Agents paid by RushMyFood' : forIso ? 'You paid your agents' : 'The ISO paid its agents'}</Typography>
            </TableCell>
            <TableCell align="right">{s.orders}</TableCell>
            <TableCell align="right">{money(s.subtotal)}</TableCell>
            <TableCell align="right">{money(s.tips)}</TableCell>
            <TableCell align="right">{money(s.gross)}</TableCell>
            <TableCell align="right">{money(s.agentShares)}</TableCell>
            <TableCell align="right">{money(s.agentBonuses)}</TableCell>
            <TableCell align="right" sx={{ color: s.carryIn < 0 || s.carryOut < 0 ? T.amberText : T.hint }}>
              {s.carryOut < 0 ? `${money(Math.abs(s.carryOut))} to next month` : s.carryIn < 0 ? `${money(Math.abs(s.carryIn))} taken` : '—'}
            </TableCell>
            <TableCell align="right" sx={{ fontWeight: 600 }}>{money(s.isoAmount)}</TableCell>
          </TableRow>
        ))}
      </TableBody>
    </Table>
  )
}
